import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './RolledOptions.css';

function RolledOptions() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedConidGroups, setExpandedConidGroups] = useState(new Set());

  useEffect(() => {
    fetchRolledOptions();
  }, []);

  const fetchRolledOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/portfolio/trades/rolled_options/`);
      console.log('Rolled options API response:', response.data);
      setGroups(response.data.groups || []);
      if (response.data.message) {
        console.log('API message:', response.data.message);
      }
    } catch (err) {
      setError('Error fetching rolled options: ' + err.message);
      console.error('Error details:', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (index) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleConidGroup = (groupIndex, conid) => {
    const key = `${groupIndex}-${conid}`;
    const newExpanded = new Set(expandedConidGroups);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedConidGroups(newExpanded);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      // Handle DD/MM/YY format
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parts[0].padStart(2, '0');
          const month = parts[1].padStart(2, '0');
          const year = '20' + parts[2];
          return `${day}/${month}/${year}`;
        }
      }
      // Handle ISO format
      const date = new Date(dateStr);
      return date.toLocaleDateString('nl-NL');
    } catch (e) {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="rolled-options-container">
        <div className="loading">Loading rolled options...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rolled-options-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="rolled-options-container">
        <div className="empty-state">
          <p>No rolled options found. Tag some options with "Rolled" to see them here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rolled-options-container">
      <div className="rolled-options-header">
        <h2>Rolled Options</h2>
        <p className="subtitle">Options grouped by underlying symbol and account</p>
      </div>

      <div className="rolled-options-table-container">
        <table className="rolled-options-table">
          <thead>
            <tr>
              <th></th>
              <th>Underlying</th>
              <th>Account</th>
              <th>Total P&L</th>
              <th>Realized P&L</th>
              <th>Unrealized P&L</th>
              <th>Trades</th>
              <th>Open</th>
              <th>Closed</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group, index) => {
              const isExpanded = expandedGroups.has(index);
              const totalPnl = parseFloat(group.total_pnl || 0);
              const pnlClass = totalPnl >= 0 ? 'positive' : 'negative';

              return (
                <React.Fragment key={`${group.underlying_symbol}-${group.account_id}-${index}`}>
                  <tr className={`group-header-row ${pnlClass}`}>
                    <td className="expand-cell">
                      <button
                        className="expand-button"
                        onClick={() => toggleGroup(index)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? '−' : '+'}
                      </button>
                    </td>
                    <td className="underlying-cell">{group.underlying_symbol}</td>
                    <td className="account-cell">{group.account_display_name}</td>
                    <td className={`pnl-cell total-pnl ${pnlClass}`}>
                      {formatCurrency(group.total_pnl)}
                    </td>
                    <td className="pnl-cell realized-pnl">
                      {formatCurrency(group.total_realized_pnl)}
                    </td>
                    <td className="pnl-cell unrealized-pnl">
                      {formatCurrency(group.total_unrealized_pnl)}
                    </td>
                    <td className="count-cell">{group.trades_count}</td>
                    <td className="count-cell open-count">{group.open_trades_count}</td>
                    <td className="count-cell closed-count">{group.closed_trades_count}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="expanded-content-row">
                      <td colSpan="9" className="expanded-content-cell">
                        <div className="conid-groups-container">
                          {group.conid_groups && group.conid_groups.map((conidGroup, conidIndex) => {
                            const conidKey = `${index}-${conidGroup.conid}`;
                            const isConidExpanded = expandedConidGroups.has(conidKey);
                            const conidPnl = parseFloat(conidGroup.total_pnl || 0);
                            const conidPnlClass = conidPnl >= 0 ? 'positive' : 'negative';
                            
                            return (
                              <div key={conidGroup.conid || conidIndex} className="conid-group">
                                <div className="conid-group-header">
                                  <button
                                    className="expand-button conid-expand-button"
                                    onClick={() => toggleConidGroup(index, conidGroup.conid)}
                                    aria-label={isConidExpanded ? 'Collapse' : 'Expand'}
                                  >
                                    {isConidExpanded ? '−' : '+'}
                                  </button>
                                  <span className="conid-group-info">
                                    <strong>CONID: {conidGroup.conid}</strong>
                                    {' - '}
                                    <span>{conidGroup.symbol}</span>
                                    {' - '}
                                    <span className={`conid-pnl ${conidPnlClass}`}>
                                      {formatCurrency(conidGroup.total_pnl)}
                                    </span>
                                    {' - '}
                                    <span>{conidGroup.trades_count} trades</span>
                                    {conidGroup.open_trades_count > 0 && (
                                      <span className="open-badge"> ({conidGroup.open_trades_count} open)</span>
                                    )}
                                  </span>
                                </div>
                                {isConidExpanded && (
                                  <div className="trades-detail-table">
                                    <table>
                                      <thead>
                                        <tr>
                                          <th>Date</th>
                                          <th>Symbol</th>
                                          <th>CONID</th>
                                          <th>Type</th>
                                          <th>Price</th>
                                          <th>Quantity</th>
                                          <th>Expiry</th>
                                          <th>P&L</th>
                                          <th>Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {conidGroup.trades.map((trade, tradeIndex) => {
                                          const tradePnl = parseFloat(trade.pnl || 0);
                                          const tradePnlClass = tradePnl >= 0 ? 'positive' : 'negative';
                                          const optionType = trade.put_call === 'C' ? 'CALL' : trade.put_call === 'P' ? 'PUT' : '-';
                                          return (
                                            <tr key={trade.id || tradeIndex} className={trade.is_open ? 'open-trade' : 'closed-trade'}>
                                              <td>{formatDate(trade.trade_date)}</td>
                                              <td>{trade.symbol || '-'}</td>
                                              <td className="conid-cell">{trade.conid || '-'}</td>
                                              <td>
                                                {trade.buy_sell === 'SELL' ? 'SELL' : 'BUY'} 
                                                {' '}
                                                {optionType}
                                                {' '}
                                                {trade.open_close_indicator === 'O' || trade.open_close_indicator === 'OPEN' ? 'OPEN' : 'CLOSE'}
                                              </td>
                                              <td>{formatCurrency(trade.trade_price)}</td>
                                              <td>{parseFloat(trade.quantity || 0).toLocaleString('nl-NL')}</td>
                                              <td>{formatDate(trade.expiry)}</td>
                                              <td className={`pnl-cell ${tradePnlClass}`}>
                                                {formatCurrency(trade.pnl)}
                                              </td>
                                              <td>
                                                <span className={`status-badge ${trade.is_open ? 'open' : 'closed'}`}>
                                                  {trade.is_open ? 'Open' : 'Closed'}
                                                </span>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RolledOptions;

